import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../api";
import type { Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS } from "./intervals";

export type Sender = Datified<Schemas["Sender"], "created_at">;
export type SenderCreate = Schemas["SenderCreate"];
export type SenderUpdate = Schemas["SenderUpdate"];
export type SMTPSettings = Schemas["SMTPSettings"];
export type TLSMode = NonNullable<SMTPSettings["tls_mode"]>;

export function useSenderList() {
  return useQuery<Sender[]>({
    queryKey: ["senders"],
    queryFn: async () => {
      const res = await apiGet<{ items: Sender[] }>("/senders");
      return res.items;
    },
    refetchInterval: POLL_BASE_MS,
  });
}

export function useCreateSender() {
  const qc = useQueryClient();
  return useMutation<Sender, unknown, SenderCreate>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<Sender>("/senders", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["senders"] }),
  });
}

export function useUpdateSender() {
  const qc = useQueryClient();
  return useMutation<Sender, unknown, { id: string; input: SenderUpdate }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, input }) => apiPut<Sender>(`/senders/${id}`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["senders"] }),
  });
}

export function useDeleteSender() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id }) => apiDelete<void>(`/senders/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["senders"] }),
  });
}

/** POST /api/senders/{id}/test — SMTP connect + auth check. */
export function useTestSender() {
  return useMutation<Schemas["OkResponse"], unknown, { id: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id }) => apiPost<Schemas["OkResponse"]>(`/senders/${id}/test`),
  });
}
